// Helpers for fields that are edited as rich HTML (via RichTextEditor) but
// sometimes need to be read as plain text — card previews, search matching,
// and the one-line "lede" pulled out of a longer description.

// Converts editor HTML to plain text, preserving paragraph/line breaks as
// newlines. Also handles legacy plain-text values (pre-dating the rich text
// editor) safely — stripping tags from text that has none is a no-op.
export function stripHtml(html) {
  if (!html) return '';
  if (typeof document === 'undefined') {
    // SSR/test fallback: no DOM to parse with, so just remove tags outright.
    return stripMarkdownMarkers(html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;?/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim());
  }

  // Turn block boundaries into real newlines *before* parsing, so textContent
  // doesn't collapse "paragraph one" and "paragraph two" into one run-on line.
  const withBreaks = html
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');

  const el = document.createElement('div');
  el.innerHTML = withBreaks;
  return stripMarkdownMarkers((el.textContent || '')
    // The editor emits &nbsp; for otherwise-blank lines. It decodes to U+00A0,
    // which trim() does not remove — so without this a visually empty line
    // still reads as content.
    .replace(/ /g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim());
}

// True once a rich text field actually has visible content — an empty editor
// still saves as "<p><br></p>", which plain truthiness/`.trim()` would treat
// as non-empty.
export function hasRichContent(html) {
  return stripHtml(html).length > 0;
}

// True when a stored value already contains markup. Everything written before
// the rich text editor existed is plain text, and the two must be handled
// differently on the way into the editor.
export function looksLikeHtml(value) {
  return /<[a-z][^>]*>/i.test(value || '');
}

// Escapes the characters that would otherwise be read as markup. Without this,
// text like "HbA1c >6.5%" or "BP <140/90" gets swallowed as a bogus tag.
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Plain text -> editor HTML, one <div> per line so line structure survives.
// Assigning raw "a\nb" to innerHTML would collapse it to a single line, since
// HTML treats newlines as ordinary whitespace — which silently flattens things
// like a stepwise "A ↓ B ↓ C" pathway into one run-on sentence.
export function textToHtml(text) {
  const lines = String(text ?? '').split(/\r?\n/);
  if (!lines.length) return '';
  return lines
    .map((line) => `<div>${line.trim() ? escapeHtml(line) : '<br>'}</div>`)
    .join('');
}

// Bullet-list fields (symptoms, causes, red flags…) stay arrays in storage —
// one entry per bullet — but are edited in the same rich text editor as
// everything else, one line per bullet. These convert between the two, keeping
// each bullet's inline formatting (<b>, <i>, <u>, <s>) intact.

export function listToHtml(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map((item) => `<div>${item}</div>`).join('');
}

export function htmlToList(html) {
  if (!html) return [];

  return String(html)
    // Block boundaries and explicit breaks each start a new bullet.
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    // Drop the opening block tags; the inline tags inside are what we keep.
    .replace(/<(p|div|li|ul|ol|h[1-6])(\s[^>]*)?>/gi, '')
    .replace(/<\/(ul|ol)\s*>/gi, '')
    .split('\n')
    .map((line) => line.trim())
    // Skip blank lines and whitespace-only artefacts ("&nbsp;", empty <b></b>).
    .filter((line) => hasRichContent(line));
}

// ---------------------------------------------------------------------------
// Paste handling
// ---------------------------------------------------------------------------

// Tags kept from a paste. Everything else is unwrapped — its text survives,
// its markup does not — so a table or a <font> from Word arrives as readable
// content rather than as a foreign layout the site never styled.
const PASTE_KEEP_TAGS = new Set([
  'P', 'DIV', 'BR', 'SPAN', 'A', 'HR',
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'SUP', 'SUB', 'MARK',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'CODE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);

// Dropped outright, contents and all.
const PASTE_DROP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD',
  'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON',
]);

// The only attributes worth carrying across. Everything else — inline colours,
// font-family, font-size, classes, ids, event handlers — is dropped: those
// look right in the source document but follow the text onto the site, where a
// pasted colour can land invisibly against the page background and a pasted
// font can drop glyphs like ↓ or β.
const PASTE_KEEP_ATTRS = { A: new Set(['href', 'title']) };

const BOLD_TAGS = new Set(['B', 'STRONG']);

// Tables are not a shape this site renders, but their text is worth keeping
// readable: each row becomes its own line rather than every cell in the table
// running together into one paragraph.
const PASTE_RENAME_TAGS = { TR: 'div' };
const PASTE_CELL_TAGS = new Set(['TD', 'TH']);

// Word, Google Docs and most web pages express emphasis as CSS on a <span>
// rather than as <b>/<i>. Since the style attribute itself is dropped, that
// emphasis has to be re-expressed as real tags or it is lost — which is
// exactly the "my bold didn't come through" complaint.
function emphasisTagsFromStyle(style) {
  const tags = [];
  const weight = (/font-weight\s*:\s*([^;]+)/i.exec(style)?.[1] || '').trim().toLowerCase();
  if (weight === 'bold' || weight === 'bolder' || parseInt(weight, 10) >= 600) tags.push('b');

  const fontStyle = (/font-style\s*:\s*([^;]+)/i.exec(style)?.[1] || '').trim().toLowerCase();
  if (fontStyle === 'italic' || fontStyle === 'oblique') tags.push('i');

  const decoration = (/text-decoration(?:-line)?\s*:\s*([^;]+)/i.exec(style)?.[1] || '').toLowerCase();
  if (decoration.includes('underline')) tags.push('u');
  if (decoration.includes('line-through')) tags.push('s');

  return tags;
}

// Google Docs wraps a whole copied selection in
// <b id="docs-internal-guid-…" style="font-weight:normal">. Taken at face
// value that bolds the entire paste, so a <b> that explicitly says it is not
// bold is treated as the wrapper it is and unwrapped.
function isUnboldedBoldTag(tag, style) {
  if (!BOLD_TAGS.has(tag)) return false;
  const weight = (/font-weight\s*:\s*([^;]+)/i.exec(style)?.[1] || '').trim().toLowerCase();
  if (!weight) return false;
  const numeric = parseInt(weight, 10);
  return weight === 'normal' || (Number.isFinite(numeric) && numeric < 600);
}

// The emphasis a tag already carries, so a <b style="font-weight:bold"> is not
// wrapped in a second, redundant <b>.
const IMPLIED_EMPHASIS = { B: 'b', STRONG: 'b', I: 'i', EM: 'i', U: 'u', S: 's', STRIKE: 's', DEL: 's' };

function cleanPastedNode(node, doc) {
  const parent = node.parentNode;
  if (!parent) return;

  if (node.nodeType === 8) { parent.removeChild(node); return; }  // comment
  if (node.nodeType !== 1) return;                                // text stays as-is

  const tag = node.tagName.toUpperCase();
  if (PASTE_DROP_TAGS.has(tag)) { parent.removeChild(node); return; }

  // Depth first, so the children are already clean when the parent is rebuilt.
  Array.from(node.childNodes).forEach((child) => cleanPastedNode(child, doc));

  if (PASTE_RENAME_TAGS[tag]) {
    const replacement = doc.createElement(PASTE_RENAME_TAGS[tag]);
    while (node.firstChild) replacement.appendChild(node.firstChild);
    parent.replaceChild(replacement, node);
    return;
  }

  // A cell's text needs a separator, or "120 mg" and "twice daily" arrive as
  // "120 mgtwice daily".
  if (PASTE_CELL_TAGS.has(tag) && node.nextSibling) {
    node.appendChild(doc.createTextNode(' '));
  }

  const style = node.getAttribute('style') || '';
  const keepTag = PASTE_KEEP_TAGS.has(tag) && !isUnboldedBoldTag(tag, style);

  // Move the children out, then wrap them in whatever emphasis the style
  // attribute was carrying before it gets thrown away.
  let inner = doc.createDocumentFragment();
  while (node.firstChild) inner.appendChild(node.firstChild);

  emphasisTagsFromStyle(style).forEach((emphasis) => {
    if (keepTag && IMPLIED_EMPHASIS[tag] === emphasis) return;
    const wrapper = doc.createElement(emphasis);
    wrapper.appendChild(inner);
    inner = wrapper;
  });

  if (keepTag) {
    const allowed = PASTE_KEEP_ATTRS[tag];
    Array.from(node.attributes).forEach((attr) => {
      if (!allowed || !allowed.has(attr.name.toLowerCase())) node.removeAttribute(attr.name);
    });
    node.appendChild(inner);
  } else {
    parent.replaceChild(inner, node);
  }
}

// Clipboard HTML -> HTML safe to store and render on the site: the author's
// bold/italic/underline/lists/headings survive, the source document's colours,
// fonts, sizes, classes and scripts do not.
export function sanitizePastedHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (!doc || !doc.body) return '';
  Array.from(doc.body.childNodes).forEach((node) => cleanPastedNode(node, doc));
  flattenPastedNewlines(doc.body, doc);
  applyMarkdownToTextNodes(doc.body, doc);
  return doc.body.innerHTML;
}

// Clipboard HTML is usually pretty-printed, with newlines between tags and
// inside paragraphs. HTML normally collapses those to a single space — but a
// single-field value renders under `white-space: pre-line`, which turns every
// one of them into a visible line break. Flattening them here keeps a pasted
// paragraph a paragraph instead of one word per line.
function flattenPastedNewlines(root, doc) {
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);

  texts.forEach((node) => {
    // A <pre> means those line breaks are the content.
    if (node.parentElement && node.parentElement.closest('pre')) return;
    if (!/[\r\n]/.test(node.nodeValue || '')) return;
    node.nodeValue = node.nodeValue.replace(/\s*\r?\n\s*/g, ' ');
  });
}

// ---------------------------------------------------------------------------
// Markdown markers
// ---------------------------------------------------------------------------

// Text copied out of ChatGPT, a README or any markdown-authored document keeps
// its emphasis as literal markers — "**bold**", "*italic*" — which would
// otherwise be typed straight into the article, stars and all. These turn the
// markers back into the formatting they stand for.
//
// Only paired markers convert, and only where the opening marker starts a word
// and the closing one ends it. That is what keeps ordinary prose intact:
// "5 * 3 * 2" and a lone "*" footnote have no pair to match, and "a*b*c" is
// not at a word boundary.
const MARKDOWN_RULES = [
  { pattern: /(^|[\s([{"'—–-])\*\*(?=\S)([^*]*[^*\s])\*\*(?=$|[\s)\]}.,;:!?"'—–-])/g, tag: 'b' },
  { pattern: /(^|[\s([{"'—–-])__(?=\S)([^_]*[^_\s])__(?=$|[\s)\]}.,;:!?"'—–-])/g, tag: 'b' },
  { pattern: /(^|[\s([{"'—–-])\*(?=\S)([^*]*[^*\s])\*(?=$|[\s)\]}.,;:!?"'—–-])/g, tag: 'i' },
  { pattern: /(^|[\s([{"'—–-])~~(?=\S)([^~]*[^~\s])~~(?=$|[\s)\]}.,;:!?"'—–-])/g, tag: 's' },
  { pattern: /(^|[\s([{"'—–-])`(?=\S)([^`]*[^`\s])`(?=$|[\s)\]}.,;:!?"'—–-])/g, tag: 'code' },
];

// Markdown markers in already-escaped text -> HTML. Bold runs first so the
// inner "*" of "**bold**" is gone before the italic rule looks at the line.
export function markdownMarkersToHtml(escapedText) {
  return MARKDOWN_RULES.reduce(
    (text, { pattern, tag }) => text.replace(pattern, (match, before, content) => `${before}<${tag}>${content}</${tag}>`),
    String(escapedText ?? ''),
  );
}

// True when the text still has a pair of markers to convert — used to leave
// text nodes (and their DOM identity) untouched when there is nothing to do.
function hasMarkdownMarkers(text) {
  return MARKDOWN_RULES.some(({ pattern }) => {
    pattern.lastIndex = 0;
    return pattern.test(text);
  });
}

// Rewrites markdown markers inside a pasted fragment's text. Rich pastes are
// not immune to this: copying a chat reply brings <strong> for some spans and
// literal "**" for others, depending on where the selection started.
function applyMarkdownToTextNodes(root, doc) {
  const texts = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) texts.push(walker.currentNode);

  texts.forEach((node) => {
    // Inside a code block the markers are the content, not formatting.
    if (node.parentElement && node.parentElement.closest('pre, code')) return;
    if (!hasMarkdownMarkers(node.nodeValue || '')) return;

    const holder = doc.createElement('span');
    holder.innerHTML = markdownMarkersToHtml(escapeHtml(node.nodeValue));
    const fragment = doc.createDocumentFragment();
    while (holder.firstChild) fragment.appendChild(holder.firstChild);
    node.parentNode.replaceChild(fragment, node);
  });
}

// Plain text -> editor HTML: the same one-<div>-per-line structure as
// textToHtml, but with markdown markers turned into real formatting. Used for
// pastes, where the source was authored in markdown more often than not.
export function markdownTextToHtml(text) {
  return String(text ?? '')
    .split(/\r?\n/)
    .map((line) => (line.trim() ? `<div>${markdownMarkersToHtml(escapeHtml(line))}</div>` : '<div><br></div>'))
    .join('');
}

// Stored values -> HTML for the site. Content written or pasted before the
// markdown handling existed — and anything an author types by hand — still
// carries its emphasis as literal "**bold**" markers, which would otherwise
// render as visible stars. Converting here means the fix covers everything
// already saved, without a migration.
export function renderRichHtml(value) {
  const html = String(value ?? '');
  // Cheap bail-out: the markers are matched per text node, where a tag no
  // longer sits between "<p>" and "__bold__" — so the test here is only
  // whether a marker character is present at all.
  if (!/[*_~`]/.test(html)) return html;
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (!doc || !doc.body) return html;
  applyMarkdownToTextNodes(doc.body, doc);
  return doc.body.innerHTML;
}

// The markers themselves, removed rather than converted — for the plain-text
// reads (card previews, search) where there is no formatting to convert into.
export function stripMarkdownMarkers(text) {
  return MARKDOWN_RULES.reduce(
    (out, { pattern }) => out.replace(pattern, (match, before, content) => `${before}${content}`),
    String(text ?? ''),
  );
}
